/* global sessionStorage */

import React, { useEffect, useState } from "react";
import HeaderPanel from "../../components/layout/HeaderPanel";
import {
  descriptionLanguage,
  escapeWithBreakLineAndTranslate,
  formatMessage,
  formatMessageWithParams,
} from "../../utils/helpers/LanguageHelper";
import { ButtonVariants } from "../../styles/fluent/ButtonVariants";
import { Link, Dropdown } from "@fluentui/react";
import { PrimaryButton } from "@fluentui/react/lib/Button";
import FooterPanel from "../../components/layout/FooterPanel";
import releaseNotes from "../../../../../assets/json/releaseNote.json";
import { Toggle } from "@fluentui/react/lib/Toggle";
import { useAppSelector, useAppDispatch } from "../../redux/hooks/hooks";
import { selectIsAuthenticated } from "../../redux/feature/settings/configurations-slice";
import { navigate } from "../../redux/feature/general/navigation-slice";
import { TROUBLESHOOTING_TOOLS } from "../../utils/constants/navigation";

const InfoCard = ({
  title,
  children,
  color,
}: {
  title: string;
  color: string;
  children: React.JSX.Element | React.JSX.Element[];
}) => (
  <div className="card" style={{ margin: "1rem 0.5rem", backgroundColor: "white" }}>
    <div
      className="card"
      style={{
        backgroundColor: color,
        borderRadius: "10px",
        width: "fit-content",
        color: "white",
        padding: "2px 20px",
      }}
    >
      {title}
    </div>
    <span style={{ marginLeft: "0.5rem" }}>{children}</span>
  </div>
);

export const TagetikInfo = () => {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const dispatch = useAppDispatch();

  const [isLogDebugEnabled, setIsLogDebugEnabled] = useState(false);

  useEffect(() => {
    const storedValue = sessionStorage.getItem("debugEnabled");
    setIsLogDebugEnabled(storedValue === "true");
  }, []);

  const handleToggleEnableLogDebug = (_event: any, checked?: boolean) => {
    setIsLogDebugEnabled(!!checked);
    sessionStorage.setItem("debugEnabled", checked ? "true" : "false");
  };

  const handleOpenTroubleshootingTools = () => {
    dispatch(navigate(TROUBLESHOOTING_TOOLS));
  };

  const [state, setState] = useState({
    releaseNoteVisible: false,
    selectedVersion: releaseNotes[0]?.version || "",
  });

  const selectedReleaseNote = releaseNotes.find((rn) => rn.version === state.selectedVersion);

  return (
    <>
      {!state.releaseNoteVisible ? (
        <>
          <HeaderPanel applyPadding>Info</HeaderPanel>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "16px",
              position: "absolute",
              transform: "translate(50%, -50%)",
              top: "55%",
              right: "50%",
            }}
          >
            <span className="brandNameText" style={{ textAlign: "center" }}>
              {formatMessage("cof.base.tagetik")}
              <br />
              {formatMessage("cof.base.intelligentdisclosure")}
            </span>
            <img src="assets/WK Logo.svg" alt="Wolters Kluwer Logo" style={{ margin: "0 25%" }} />
            <span className="versionNumberText" style={{ textAlign: "center" }}>
              {formatMessageWithParams("cof.info.versionnumber", { versionNumber: state.selectedVersion })}
            </span>
            <Link
              style={{ textDecoration: "none" }}
              onClick={() => setState({ ...state, releaseNoteVisible: true })}
              className={ButtonVariants.link}
            >
              {formatMessage("cof.info.releasenote")}
            </Link>
          </div>
          <FooterPanel>
            {isAuthenticated ? (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                <PrimaryButton
                  onClick={handleOpenTroubleshootingTools}
                  iconProps={{ iconName: "DeveloperTools" }}
                  styles={{
                    root: {
                      backgroundColor: "var(--color-blue)",
                      borderColor: "var(--color-blue)",
                    },
                    rootHovered: {
                      backgroundColor: "var(--color-darkblue)",
                      borderColor: "var(--color-darkblue)",
                    },
                  }}
                />
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <span style={{ fontSize: "14px" }}>{formatMessage("cof.info.action.enableDebug")}</span>
                  <Toggle
                    checked={isLogDebugEnabled}
                    onChange={handleToggleEnableLogDebug}
                    styles={{ root: { marginBottom: 0 } }}
                  />
                </div>
              </div>
            ) : null}
          </FooterPanel>
        </>
      ) : (
        <>
          <HeaderPanel applyPadding>Release Note</HeaderPanel>
          <Dropdown
            label="Version"
            options={releaseNotes.map((rn) => ({
              key: rn.version,
              text:
                rn.version +
                " - " +
                new Date(rn.date).toLocaleDateString(descriptionLanguage() ?? "default", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                }),
            }))}
            selectedKey={state.selectedVersion}
            onChange={(_event, option) => setState({ ...state, selectedVersion: option.key.toString() })}
            styles={{
              dropdown: { width: 200, marginLeft: "0.5rem", borderRadius: "10px" },
              label: { margin: "0.5rem 0rem 0rem 0.5rem" },
            }}
          />
          {selectedReleaseNote?.new?.length ? (
            <InfoCard title="NEW" color="var(--color-purple)">
              {escapeWithBreakLineAndTranslate(selectedReleaseNote.new)}
            </InfoCard>
          ) : null}
          {selectedReleaseNote?.fixed?.length ? (
            <InfoCard title="FIXED" color="var(--color-green)">
              {escapeWithBreakLineAndTranslate(selectedReleaseNote.fixed)}
            </InfoCard>
          ) : null}
          <FooterPanel>
            <PrimaryButton
              className={ButtonVariants.white}
              text={formatMessage("cof.base.back")}
              style={{ float: "left" }}
              onClick={() => setState({ ...state, releaseNoteVisible: false })}
            />
          </FooterPanel>
        </>
      )}
    </>
  );
};
